import type { ConciseBranch } from '@festina/configuration-general-types/configuration'
import {
  type BranchKey,
  useCreateOrUpdateConciseBranch,
  useInvalidateAllConciseBranches,
} from '@festina-life/api'
import { ErrorMessage } from '@festina-life/components'
import {
  assertNotEmpty,
  notEmpty,
  type Optional,
  typeUtils,
} from '@festina-life/core'
import { toast } from '@festina-life/ui/feedback'
import { FormikForm, FormikSelect } from '@festina-life/ui/formikForms'
import { useTranslate } from '@festina-life/ui/hooks'
import { Stack, useClose } from '@festina-life/ui/structure'
import { useState } from 'react'

import { useConciseBranch } from '../../../../uiModels/branches/useConciseBranch'
import { OperationsLoadedButton } from '../../../operationsButton/OperationsLoadedButton'
import { useFollowBranchCandidates } from './useFollowBranchCandidates'

type FollowBranchInput = {
  followsBranch?: Optional<ConciseBranch>
}

const validate = typeUtils.createValidator<FollowBranchInput>()

type Props = {
  branchKey: BranchKey
}

export const ChangeFollowBranchForm = ({ branchKey }: Props) => {
  const translate = useTranslate()
  const [error, setError] = useState<Optional<Error>>()
  const close = useClose()
  const { mutateAsync: updateBranch, isPending } =
    useCreateOrUpdateConciseBranch()
  const invalidateAllConciseBranches = useInvalidateAllConciseBranches()
  const currentConciseBranch = useConciseBranch({
    server: branchKey.server,
    branchName: branchKey.name,
    shouldFallbackToStandard: false,
  })
  const followsConciseBranch = useConciseBranch({
    server: branchKey.server,
    branchName: currentConciseBranch?.followsBranchName,
    shouldFallbackToStandard: false,
  })

  assertNotEmpty(currentConciseBranch)

  const followableBranches = useFollowBranchCandidates({
    branchName: branchKey.name,
    server: branchKey.server,
    baseBranchName: currentConciseBranch.baseBranchName,
    category: currentConciseBranch.branchCategory ?? 'none',
  })

  const handleSubmit = async ({ followsBranch }: FollowBranchInput) => {
    setError(undefined)
    assertNotEmpty(currentConciseBranch.name)
    assertNotEmpty(currentConciseBranch.branchCategory)

    try {
      await updateBranch({
        followsBranchName: followsBranch?.name ?? undefined,
        server: branchKey.server,
        category: currentConciseBranch.branchCategory,
        branchName: currentConciseBranch.name,
        baseBranchName: currentConciseBranch.baseBranchName,
        typeVersions: currentConciseBranch.typesVersionIds?.filter(notEmpty),
      })

      await invalidateAllConciseBranches()

      toast({
        title: translate('branchesTree.follows.success', {
          branchName: branchKey.name,
          followsBranchName: followsBranch?.name ?? translate('none'),
        }),
        variant: 'success',
      })
      close()
    } catch (error) {
      setError(error)
    }
  }

  return (
    <FormikForm
      initialValues={{ followsBranch: followsConciseBranch ?? undefined }}
      onSubmit={handleSubmit}
    >
      <FormikSelect
        options={followableBranches}
        name={validate('followsBranch')}
        label={translate('followBranchForm.followsBranchName.label')}
        renderFn={(specification) => specification.name ?? ''}
        keyFn={(specification) => specification.name ?? ''}
      />

      {error && <ErrorMessage error={error} />}

      <Stack className="w-full justify-end">
        <OperationsLoadedButton
          type="submit"
          variant="primary"
          isLoading={isPending}
        >
          {translate('branchDetailsPanel.tabs.manage.changeFollowBranch')}
        </OperationsLoadedButton>
      </Stack>
    </FormikForm>
  )
}
